import {useState} from 'react'
import {Check, Edit, Settings, X} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {SecretReferenceInput} from '@/components/shared/SecretReferenceInput'
import {Switch} from '@/components/ui/switch'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Strong} from '@/components/shared/Strong'
import {MutedText} from '@/components/shared/MutedText'
import {FormGrid} from '@/components/shared/FormGrid'
import {Stack} from '@/components/shared/Stack'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {CodeText} from '@/components/shared/CodeText'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {DirectoryDialect} from '@/types/outbound-connector'
import type {OutboundConnector, LdapOutboundConfigCreateRequest, LdapOutboundConfigUpdateRequest} from '@/types/outbound-connector'


const DEFAULT_USER_SEARCH_FILTER = '(sAMAccountName={0})'
const DEFAULT_GROUP_MEMBER_ATTRIBUTE = 'member'
const DEFAULT_USER_DN_ATTRIBUTE = 'distinguishedName'

const DIRECTORY_DIALECT_OPTIONS = [
    {value: DirectoryDialect.ACTIVE_DIRECTORY, labelKey: 'dialectActiveDirectory' as const},
    {value: DirectoryDialect.OPEN_LDAP, labelKey: 'dialectOpenLdap' as const},
] as const


interface LdapFormState {
    host: string
    port: number
    useSsl: boolean
    useStarttls: boolean
    bindDn: string
    bindPasswordRef: string
    baseDn: string
    userSearchBase: string
    userSearchFilter: string
    groupSearchBase: string
    groupMemberAttribute: string
    userDnAttribute: string
    connectTimeoutMs: number
    responseTimeoutMs: number
    poolSize: number
    directoryDialect: DirectoryDialect
    version: number
}

const DEFAULT_LDAP_FORM: LdapFormState = {
    host: '',
    port: 389,
    useSsl: false,
    useStarttls: false,
    bindDn: '',
    bindPasswordRef: '',
    baseDn: '',
    userSearchBase: '',
    userSearchFilter: DEFAULT_USER_SEARCH_FILTER,
    groupSearchBase: '',
    groupMemberAttribute: DEFAULT_GROUP_MEMBER_ATTRIBUTE,
    userDnAttribute: DEFAULT_USER_DN_ATTRIBUTE,
    connectTimeoutMs: 5000,
    responseTimeoutMs: 10000,
    poolSize: 5,
    directoryDialect: DirectoryDialect.ACTIVE_DIRECTORY,
    version: 0,
}

interface LdapConfigCardProps {
    connector: OutboundConnector
    canUpdate: boolean
}

export function LdapConfigCard({connector, canUpdate}: LdapConfigCardProps) {
    const services = useServices()
    const i18n = useI18n()
    const createDialog = useDialog()
    const editDialog = useDialog()
    const [form, setForm] = useState<LdapFormState>(DEFAULT_LDAP_FORM)

    const updateForm = (updates: Partial<LdapFormState>) => setForm((prev) => ({...prev, ...updates}))

    const createMutation = useEntityMutation({
        mutationFn: (data: LdapOutboundConfigCreateRequest) =>
            services.outboundConnectors.createLdapConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: () => createDialog.close(),
    })

    const updateMutation = useEntityMutation({
        mutationFn: (data: LdapOutboundConfigUpdateRequest) =>
            services.outboundConnectors.updateLdapConfig(connector.id, data),
        invalidate: ['connector', connector.id],
        successMessage: i18n.connectors.configurationSaved,
        closeDialog: () => editDialog.close(),
    })

    const openCreate = () => {
        setForm(DEFAULT_LDAP_FORM)
        createDialog.open()
    }

    const openEdit = () => {
        const config = connector.ldapConfig
        if (!config) return
        setForm({
            host: config.host,
            port: config.port,
            useSsl: config.useSsl,
            useStarttls: config.useStarttls,
            bindDn: config.bindDn,
            bindPasswordRef: '',
            baseDn: config.baseDn,
            userSearchBase: config.userSearchBase ?? '',
            userSearchFilter: config.userSearchFilter ?? DEFAULT_USER_SEARCH_FILTER,
            groupSearchBase: config.groupSearchBase ?? '',
            groupMemberAttribute: config.groupMemberAttribute ?? DEFAULT_GROUP_MEMBER_ATTRIBUTE,
            userDnAttribute: config.userDnAttribute ?? DEFAULT_USER_DN_ATTRIBUTE,
            connectTimeoutMs: config.connectTimeoutMs,
            responseTimeoutMs: config.responseTimeoutMs,
            poolSize: config.poolSize,
            directoryDialect: config.directoryDialect ?? DirectoryDialect.ACTIVE_DIRECTORY,
            version: config.version,
        })
        editDialog.open()
    }

    const handleCreate = () => {
        const request: LdapOutboundConfigCreateRequest = {
            host: form.host,
            port: form.port,
            useSsl: form.useSsl,
            useStarttls: form.useStarttls,
            bindDn: form.bindDn,
            bindPasswordRef: form.bindPasswordRef,
            baseDn: form.baseDn,
            userSearchBase: form.userSearchBase || undefined,
            userSearchFilter: form.userSearchFilter || undefined,
            groupSearchBase: form.groupSearchBase || undefined,
            groupMemberAttribute: form.groupMemberAttribute || undefined,
            userDnAttribute: form.userDnAttribute || undefined,
            connectTimeoutMs: form.connectTimeoutMs,
            responseTimeoutMs: form.responseTimeoutMs,
            poolSize: form.poolSize,
            directoryDialect: form.directoryDialect,
        }
        createMutation.mutate(request)
    }

    const handleUpdate = () => {
        const request: LdapOutboundConfigUpdateRequest = {
            host: form.host,
            port: form.port,
            useSsl: form.useSsl,
            useStarttls: form.useStarttls,
            bindDn: form.bindDn,
            bindPasswordRef: form.bindPasswordRef,
            baseDn: form.baseDn,
            userSearchBase: form.userSearchBase || null,
            userSearchFilter: form.userSearchFilter || null,
            groupSearchBase: form.groupSearchBase || null,
            groupMemberAttribute: form.groupMemberAttribute || DEFAULT_GROUP_MEMBER_ATTRIBUTE,
            userDnAttribute: form.userDnAttribute || DEFAULT_USER_DN_ATTRIBUTE,
            connectTimeoutMs: form.connectTimeoutMs,
            responseTimeoutMs: form.responseTimeoutMs,
            poolSize: form.poolSize,
            directoryDialect: form.directoryDialect,
            version: form.version,
        }
        updateMutation.mutate(request)
    }

    const dialectLabel = (dialect: DirectoryDialect): string => {
        const option = DIRECTORY_DIALECT_OPTIONS.find(o => o.value === dialect)
        return option ? i18n.connectors[option.labelKey] : dialect
    }

    const isFormValid = () => !!form.host && !!form.bindDn && !!form.baseDn

    const ldapConfig = connector.ldapConfig

    const formFields = (mode: 'create' | 'edit') => {
        const handleSubmit = () => {
            if (mode === 'create') {
                handleCreate()
            } else {
                handleUpdate()
            }
        }
        return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit() }}>
            <div className="grid gap-6 py-4">
                <Stack>
                    <Strong>{i18n.connectors.serverSettings}</Strong>
                    <FormGrid>
                        <DetailGrid columns={3} responsive={false}>
                            <div className="grid gap-2 col-span-2">
                                <Label htmlFor="ldap-host">{i18n.connectors.host}</Label>
                                <Input id="ldap-host" value={form.host}
                                    onChange={(e) => updateForm({host: e.target.value})}
                                    placeholder={i18n.connectors.hostPlaceholder}/>
                            </div>
                            <FormGrid>
                                <Label htmlFor="ldap-port">{i18n.connectors.port}</Label>
                                <Input id="ldap-port" type="number" value={form.port}
                                    onChange={(e) => updateForm({port: parseInt(e.target.value)})}/>
                            </FormGrid>
                        </DetailGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <InlineRow justify="between">
                                <Label htmlFor="ldap-useSsl">{i18n.connectors.useSsl}</Label>
                                <Switch id="ldap-useSsl" checked={form.useSsl}
                                    onCheckedChange={(checked) => updateForm({useSsl: checked, port: checked ? 636 : 389})}/>
                            </InlineRow>
                            <InlineRow justify="between">
                                <Label htmlFor="ldap-useStarttls">{i18n.connectors.useStarttls}</Label>
                                <Switch id="ldap-useStarttls" checked={form.useStarttls}
                                    onCheckedChange={(checked) => updateForm({useStarttls: checked})}/>
                            </InlineRow>
                        </DetailGrid>
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.authentication}</Strong>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-bindDn">{i18n.connectors.bindDn}</Label>
                            <Input id="ldap-bindDn" value={form.bindDn}
                                onChange={(e) => updateForm({bindDn: e.target.value})}
                                placeholder={i18n.connectors.bindDnPlaceholder}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-bindPasswordRef">{i18n.connectors.bindPasswordRef}</Label>
                            <SecretReferenceInput
                                id="ldap-bindPasswordRef"
                                value={form.bindPasswordRef}
                                onChange={(v) => updateForm({bindPasswordRef: v})}
                                placeholder={i18n.connectors.bindPasswordRefPlaceholder}
                                data-testid="bind-password-ref"
                            />
                            <MutedText size="sm">{i18n.connectors.bindPasswordRefHint}</MutedText>
                        </FormGrid>
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.directorySettings}</Strong>
                    <FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-directoryDialect">{i18n.connectors.directoryDialect}</Label>
                            <Select value={form.directoryDialect}
                                onValueChange={(v) => updateForm({directoryDialect: v as DirectoryDialect})}>
                                <SelectTrigger id="ldap-directoryDialect" data-testid="select-directory-dialect"><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    {DIRECTORY_DIALECT_OPTIONS.map(({value, labelKey}) => (
                                        <SelectItem key={value} value={value} data-testid={`dialect-option-${value}`}>
                                            {i18n.connectors[labelKey]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <MutedText size="sm">{i18n.connectors.directoryDialectHint}</MutedText>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-baseDn">{i18n.connectors.baseDn}</Label>
                            <Input id="ldap-baseDn" value={form.baseDn}
                                onChange={(e) => updateForm({baseDn: e.target.value})}
                                placeholder={i18n.connectors.baseDnPlaceholder}/>
                        </FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="ldap-userSearchBase">{i18n.connectors.userSearchBase}</Label>
                                <Input id="ldap-userSearchBase" value={form.userSearchBase}
                                    onChange={(e) => updateForm({userSearchBase: e.target.value})}
                                    placeholder={i18n.connectors.userSearchBasePlaceholder}/>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="ldap-groupSearchBase">{i18n.connectors.groupSearchBase}</Label>
                                <Input id="ldap-groupSearchBase" value={form.groupSearchBase}
                                    onChange={(e) => updateForm({groupSearchBase: e.target.value})}
                                    placeholder={i18n.connectors.groupSearchBasePlaceholder}/>
                            </FormGrid>
                        </DetailGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-userSearchFilter">{i18n.connectors.userSearchFilter}</Label>
                            <Input id="ldap-userSearchFilter" value={form.userSearchFilter}
                                onChange={(e) => updateForm({userSearchFilter: e.target.value})}
                                placeholder={i18n.connectors.userSearchFilterPlaceholder}/>
                        </FormGrid>
                        <DetailGrid columns={2} responsive={false}>
                            <FormGrid>
                                <Label htmlFor="ldap-groupMemberAttribute">{i18n.connectors.groupMemberAttribute}</Label>
                                <Input id="ldap-groupMemberAttribute" value={form.groupMemberAttribute}
                                    onChange={(e) => updateForm({groupMemberAttribute: e.target.value})}/>
                            </FormGrid>
                            <FormGrid>
                                <Label htmlFor="ldap-userDnAttribute">{i18n.connectors.userDnAttribute}</Label>
                                <Input id="ldap-userDnAttribute" value={form.userDnAttribute}
                                    onChange={(e) => updateForm({userDnAttribute: e.target.value})}/>
                            </FormGrid>
                        </DetailGrid>
                    </FormGrid>
                </Stack>
                <Stack>
                    <Strong>{i18n.connectors.connectionSettings}</Strong>
                    <DetailGrid columns={3} responsive={false}>
                        <FormGrid>
                            <Label htmlFor="ldap-connectTimeoutMs">{i18n.connectors.connectTimeoutMs}</Label>
                            <Input id="ldap-connectTimeoutMs" type="number" value={form.connectTimeoutMs}
                                onChange={(e) => updateForm({connectTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-responseTimeoutMs">{i18n.connectors.responseTimeoutMs}</Label>
                            <Input id="ldap-responseTimeoutMs" type="number" value={form.responseTimeoutMs}
                                onChange={(e) => updateForm({responseTimeoutMs: parseInt(e.target.value)})}/>
                        </FormGrid>
                        <FormGrid>
                            <Label htmlFor="ldap-poolSize">{i18n.connectors.poolSize}</Label>
                            <Input id="ldap-poolSize" type="number" value={form.poolSize}
                                onChange={(e) => updateForm({poolSize: parseInt(e.target.value)})}/>
                        </FormGrid>
                    </DetailGrid>
                </Stack>
            </div>
            <SaveCancelFooter
                testIdPrefix={mode === 'create' ? 'create-ldap' : 'edit-ldap'}
                onCancel={() => mode === 'create' ? createDialog.close() : editDialog.close()}
                saving={mode === 'create' ? createMutation.isPending : updateMutation.isPending}
                disabled={!isFormValid() || (mode === 'create' && !form.bindPasswordRef)}
                saveLabel={mode === 'create' ? i18n.connectors.saveConfiguration : i18n.common.saveChanges}
            />
        </form>
        )
    }

    return (
        <>
            <Card data-testid="ldap-config-card">
                <RowCardHeader>
                    <div>
                        <CardTitle className="items-center gap-2" data-testid="card-title-ldap-config">
                            <Settings className="h-5 w-5"/>
                            {i18n.connectors.ldapConfiguration}
                        </CardTitle>
                        <CardDescription>{i18n.connectors.ldapDescription}</CardDescription>
                    </div>
                    {canUpdate && (
                        ldapConfig ? (
                            <Button data-testid="btn-edit-ldap-config" size="sm" onClick={openEdit}>
                                <Icon icon={Edit} className="mr-2"/>
                                {i18n.common.edit}
                            </Button>
                        ) : (
                            <Button data-testid="btn-create-ldap-config" size="sm" onClick={openCreate}>
                                <Icon icon={Settings} className="mr-2"/>
                                {i18n.connectors.configure}
                            </Button>
                        )
                    )}
                </RowCardHeader>
                <CardContent>
                    {ldapConfig ? (
                        <dl className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.host}</dt>
                                <dd><CodeText>{ldapConfig.host}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.port}</dt>
                                <dd><CodeText>{ldapConfig.port}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.useSsl}</dt>
                                <dd className="mt-1 flex items-center gap-1">
                                    {ldapConfig.useSsl
                                        ? <><Check className="h-4 w-4 text-status-success"/><span>{i18n.common.yes}</span></>
                                        : <><Icon icon={X} muted/><span>{i18n.common.no}</span></>}
                                </dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.useStarttls}</dt>
                                <dd className="mt-1 flex items-center gap-1">
                                    {ldapConfig.useStarttls
                                        ? <><Check className="h-4 w-4 text-status-success"/><span>{i18n.common.yes}</span></>
                                        : <><Icon icon={X} muted/><span>{i18n.common.no}</span></>}
                                </dd>
                            </div>
                            <div className="lg:col-span-2">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.bindDn}</dt>
                                <dd><CodeText>{ldapConfig.bindDn}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.directoryDialect}</dt>
                                <dd className="mt-1" data-testid="dl-directory-dialect">{dialectLabel(ldapConfig.directoryDialect)}</dd>
                            </div>
                            <div className="lg:col-span-3">
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.baseDn}</dt>
                                <dd><CodeText>{ldapConfig.baseDn}</CodeText></dd>
                            </div>
                            {ldapConfig.userSearchBase && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.userSearchBase}</dt>
                                    <dd><CodeText>{ldapConfig.userSearchBase}</CodeText></dd>
                                </div>
                            )}
                            {ldapConfig.userSearchFilter && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.userSearchFilter}</dt>
                                    <dd><CodeText>{ldapConfig.userSearchFilter}</CodeText></dd>
                                </div>
                            )}
                            {ldapConfig.groupSearchBase && (
                                <div>
                                    <dt className="font-medium text-muted-foreground">{i18n.connectors.groupSearchBase}</dt>
                                    <dd><CodeText>{ldapConfig.groupSearchBase}</CodeText></dd>
                                </div>
                            )}
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.groupMemberAttribute}</dt>
                                <dd><CodeText>{ldapConfig.groupMemberAttribute}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.userDnAttribute}</dt>
                                <dd><CodeText>{ldapConfig.userDnAttribute}</CodeText></dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.connectTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: ldapConfig.connectTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.responseTimeoutMs}</dt>
                                <dd className="mt-1">{i18n.common.valueWithMs({value: ldapConfig.responseTimeoutMs})}</dd>
                            </div>
                            <div>
                                <dt className="font-medium text-muted-foreground">{i18n.connectors.poolSize}</dt>
                                <dd className="mt-1">{ldapConfig.poolSize}</dd>
                            </div>
                        </dl>
                    ) : (
                        <MutedText block centered className="py-4">{i18n.connectors.ldapNotConfigured}</MutedText>
                    )}
                </CardContent>
            </Card>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.configureLdapConnection}</DialogTitle>
                        <DialogDescription>{i18n.connectors.configureLdapDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('create')}
                </DialogContent>
            </Dialog>

            <Dialog open={editDialog.isOpen} onOpenChange={editDialog.bind.onOpenChange}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{i18n.connectors.editLdapConfiguration}</DialogTitle>
                        <DialogDescription>{i18n.connectors.editLdapDescription}</DialogDescription>
                    </DialogHeader>
                    {formFields('edit')}
                </DialogContent>
            </Dialog>
        </>
    )
}
